import {
  BOARD_STAGES,
  canTenant,
  PROCUREMENT_STAGES,
  type PurchaseRequestDetail,
  type PurchaseRequestSummary,
  STAGE_SLA_STAGES,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, Settings, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useCostArticles } from '../../lib/articles';
import { date, plural } from '../../lib/format';
import { mln, projectsQuery } from '../../lib/projects';
import { useCan, useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { PaymentRequestsTab, ProcurementOverviewStrip, RegistriesTab } from './payments';
import { RfqsTab } from './RfqPage';

export const requestsQuery = (projectId?: string, scope: 'active' | 'all' = 'active', q?: string) => ({
  queryKey: ['procurement', 'requests', projectId ?? 'all', scope, q ?? ''],
  queryFn: () => api<PurchaseRequestSummary[]>('/procurement/requests', { query: { projectId, scope, q } }),
});

function parseNum(v: string): number {
  return Number(v.replace(/\s/g, '').replace(',', '.'));
}

type LineDraft = {
  id?: string;
  name: string;
  unit: string;
  quantity: string;
  estimatePrice: string;
  /** Примечание к позиции: в диалоге не правится, но сохраняется при правке */
  note?: string | null;
};

/** Новая заявка или правка своей заявки до анализа КП (в том числе после возврата на доработку) */
export function NewRequestDialog({
  open,
  onOpenChange,
  projectId,
  edit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId?: string;
  edit?: PurchaseRequestDetail;
}) {
  const costArticles = useCostArticles();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe();
  // Сметную цену существующих позиций правит тот, кто её видит; без доступа к ценам она сохраняется прежней
  const showPrice = !edit || seesMoney(me.access, 'acts_invoices');
  const projects = useQuery({ ...projectsQuery('active'), enabled: open && !edit });
  const [f, setF] = useState(() => ({
    projectId: edit?.projectId ?? projectId ?? '',
    title: edit?.title ?? '',
    costArticle: edit ? (edit.costArticle ?? '') : 'Материалы',
    requiredDate: edit?.requiredDate ?? '',
    note: edit?.note ?? '',
  }));
  const [lines, setLines] = useState<LineDraft[]>(() =>
    edit
      ? edit.items.map((i) => ({
          id: i.id,
          name: i.name,
          unit: i.unit,
          quantity: String(i.quantity),
          estimatePrice: i.estimatePrice === null ? '' : String(i.estimatePrice),
          note: i.note,
        }))
      : [{ name: '', unit: 'т', quantity: '', estimatePrice: '' }],
  );
  const body = () => ({
    title: f.title,
    costArticle: f.costArticle || null,
    requiredDate: f.requiredDate || null,
    note: f.note || null,
    lines: lines
      .filter((l) => l.name.trim())
      .map((l) => ({
        ...(l.id ? { id: l.id, note: l.note ?? null } : {}),
        name: l.name,
        unit: l.unit,
        quantity: parseNum(l.quantity),
        estimatePrice: l.estimatePrice ? parseNum(l.estimatePrice) : null,
      })),
  });
  const create = useMutation({
    mutationFn: () =>
      edit
        ? api<PurchaseRequestDetail>(`/procurement/requests/${edit.id}`, { method: 'PATCH', body: body() })
        : api<PurchaseRequestDetail>('/procurement/requests', {
            body: { projectId: f.projectId, ...body() },
          }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['procurement'] });
      if (edit) {
        qc.setQueryData(['procurement', 'request', r.id], r);
        toast(`Заявка ${r.number} сохранена`);
        onOpenChange(false);
        return;
      }
      toast(`Заявка ${r.number} создана`);
      onOpenChange(false);
      navigate({ to: `/procurement/${r.id}` });
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      title={edit ? `Заявка ${edit.number}` : 'Новая заявка на закуп'}
      description={
        edit
          ? 'Позиции меняются до анализа КП. После правки отправьте заявку на проверку снова.'
          : 'Позиции, количество и сметная цена; заявка уходит на проверку полноты.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={
              !f.projectId || f.title.trim().length < 3 || !lines.some((l) => l.name.trim() && l.quantity)
            }
            onClick={() => create.mutate()}
          >
            {edit ? 'Сохранить заявку' : 'Создать заявку'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект">
          <Select
            value={f.projectId}
            disabled={Boolean(edit)}
            onChange={(e) => setF({ ...f, projectId: e.target.value })}
          >
            {edit ? (
              <option value={edit.projectId}>
                {edit.projectCode} {edit.projectName}
              </option>
            ) : null}
            <option value="">— выберите —</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Что закупаем">
          <Input
            value={f.title}
            placeholder="Арматура для камер водовода"
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Field>
        <Field label="Статья финансового плана">
          <Select value={f.costArticle} onChange={(e) => setF({ ...f, costArticle: e.target.value })}>
            {edit && !edit.costArticle ? <option value="">— не указана —</option> : null}
            {costArticles.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Нужно на объекте к">
          <Input
            type="date"
            value={f.requiredDate}
            onChange={(e) => setF({ ...f, requiredDate: e.target.value })}
          />
        </Field>
      </div>
      <h3 className="mt-5 mb-2 text-[14px] font-semibold text-ink">Позиции</h3>
      <div className="flex flex-col gap-2">
        {lines.map((l, i) => (
          <div
            key={l.id ?? i}
            className="grid grid-cols-[64px_minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 sm:grid-cols-[minmax(0,1fr)_70px_100px_130px_auto]"
          >
            <Input
              className="col-span-4 sm:col-span-1"
              aria-label="Наименование"
              placeholder="Арматура Ø12 А500С"
              value={l.name}
              onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
            />
            <Input
              aria-label="Ед."
              value={l.unit}
              onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)))}
            />
            <Input
              aria-label="Количество"
              inputMode="decimal"
              placeholder="кол-во"
              value={l.quantity}
              onChange={(e) =>
                setLines(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))
              }
            />
            {showPrice || !l.id ? (
              <Input
                aria-label="Сметная цена"
                inputMode="decimal"
                placeholder="цена сметы, ₸"
                value={l.estimatePrice}
                onChange={(e) =>
                  setLines(lines.map((x, j) => (j === i ? { ...x, estimatePrice: e.target.value } : x)))
                }
              />
            ) : (
              <span className="self-center text-[13px] text-ink-2">цена сохранится</span>
            )}
            <IconButton label="Убрать позицию" onClick={() => setLines(lines.filter((_, j) => j !== i))}>
              <Trash2 className="size-4" />
            </IconButton>
          </div>
        ))}
      </div>
      <Button
        className="mt-2"
        size="sm"
        variant="secondary"
        icon={<Plus className="size-4" />}
        onClick={() =>
          setLines([
            ...lines,
            { name: '', unit: lines.at(-1)?.unit ?? 'шт', quantity: '', estimatePrice: '' },
          ])
        }
      >
        Позиция
      </Button>
      <Field label="Примечание" className="mt-4">
        <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      <ErrorBox error={create.error} className="mt-4" />
    </Dialog>
  );
}

function Card({ r }: { r: PurchaseRequestSummary }) {
  return (
    <Link
      to={`/procurement/${r.id}`}
      className={clsx(
        'block rounded-md border bg-surface p-3 transition-colors hover:border-ink-3',
        r.overdue ? 'border-danger/50' : 'border-line',
      )}
    >
      <div className="flex items-center justify-between gap-2 text-[12px] text-ink-2">
        <span className="num">{r.number}</span>
        <span className={clsx('num whitespace-nowrap', r.overdue && 'font-medium text-danger')}>
          {r.daysOnStage} {plural(r.daysOnStage, 'день', 'дня', 'дней').split(' ')[1]}
          {r.slaDays ? ` из ${r.slaDays}` : ''}
        </span>
      </div>
      <p className="mt-1 text-[14px] leading-5 font-medium text-ink">{r.title}</p>
      <p className="mt-1 text-[12px] text-ink-2">
        {r.projectCode} · {plural(r.lines, 'позиция', 'позиции', 'позиций')} ·{' '}
        {plural(r.offers, 'КП', 'КП', 'КП')}
      </p>
      {r.estimateTotal !== null ? (
        <p className="num mt-1 text-[12px] text-ink-2">
          смета {mln(r.estimateTotal)} · лучшее {mln(r.bestOfferTotal)} млн ₸
        </p>
      ) : null}
      {r.selectedSupplier ? <p className="mt-1 text-[12px] text-ink">выбран: {r.selectedSupplier}</p> : null}
      {r.registryNumber || r.paymentStatus !== 'none' ? (
        <p className="mt-1 text-[12px] text-ink-2">
          {r.registryNumber ? `реестр ${r.registryNumber}` : ''}
          {r.paymentStatus === 'partial' ? `${r.registryNumber ? ' · ' : ''}оплачено частично` : ''}
          {r.paymentStatus === 'full' ? `${r.registryNumber ? ' · ' : ''}оплачено полностью` : ''}
        </p>
      ) : null}
    </Link>
  );
}

export function RequestsBoard({ projectId, search }: { projectId?: string; search?: string }) {
  const q = useQuery(requestsQuery(projectId, 'active', search || undefined));
  const [view, setView] = useState<'board' | 'list'>('board');
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return search ? (
      <EmptyState title="Ничего не нашлось">
        Ищите по номеру заявки, материалу, поставщику, номеру договора, реестра или номеру в 1С.
      </EmptyState>
    ) : (
      <EmptyState title="Заявок нет">
        Создайте заявку на закуп: позиции, количество и сметная цена.
      </EmptyState>
    );
  const overdue = q.data.filter((r) => r.overdue).length;
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border border-line bg-surface p-0.5">
          {(
            [
              ['board', 'Доска'],
              ['list', 'Список'],
            ] as const
          ).map(([k, t]) => (
            <button
              key={k}
              type="button"
              onClick={() => setView(k)}
              className={clsx(
                'h-8 rounded-[8px] px-3 text-[13px] font-medium',
                view === k ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
              )}
            >
              {t}
            </button>
          ))}
        </div>
        <span className="text-[13px] text-ink-2">
          {plural(q.data.length, 'заявка', 'заявки', 'заявок')} в работе
          {overdue ? `, просрочено ${overdue}` : ''}
        </span>
      </div>
      {view === 'board' && !search ? (
        <div className="overflow-x-auto pb-2">
          <div className="grid auto-cols-[248px] grid-flow-col gap-3">
            {BOARD_STAGES.map((s) => {
              const list = q.data.filter((r) => r.stage === s || (s === 'new' && r.stage === 'returned'));
              return (
                <div key={s} className="flex min-w-0 flex-col gap-2">
                  <div className="flex h-10 items-start justify-between gap-2 border-b border-line px-1">
                    <span className="text-[13px] font-semibold text-ink">{PROCUREMENT_STAGES[s]}</span>
                    <span className="num text-[12px] text-ink-2">{list.length}</span>
                  </div>
                  {list.map((r) => (
                    <Card key={r.id} r={r} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <Panel bodyClassName="p-0">
          <ScrollX>
            <table className="w-full min-w-[900px] text-[14px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2.5 pr-3 pl-5 font-medium">Заявка</th>
                  <th className="py-2.5 pr-3 font-medium">Проект</th>
                  <th className="py-2.5 pr-3 font-medium">Этап</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Смета, млн ₸</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Лучшее КП</th>
                  <th className="py-2.5 pr-3 font-medium">КП</th>
                  <th className="py-2.5 pr-3 font-medium">Реестр, оплата</th>
                  <th className="py-2.5 pr-5 font-medium">Нужно к</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((r) => (
                  <tr key={r.id} className="border-t border-line">
                    <td className="py-2.5 pr-3 pl-5">
                      <Link to={`/procurement/${r.id}`} className="font-medium text-ink hover:underline">
                        {r.number} {r.title}
                      </Link>
                    </td>
                    <td className="py-2.5 pr-3 text-ink-2">{r.projectCode}</td>
                    <td className="py-2.5 pr-3">
                      <Badge tone={r.overdue ? 'danger' : 'neutral'}>{PROCUREMENT_STAGES[r.stage]}</Badge>
                    </td>
                    <td className="num py-2.5 pr-3 text-right">{mln(r.estimateTotal)}</td>
                    <td className="num py-2.5 pr-3 text-right">{mln(r.bestOfferTotal)}</td>
                    <td className="num py-2.5 pr-3">{r.offers}</td>
                    <td className="py-2.5 pr-3 text-[13px] text-ink-2">
                      {r.registryNumber ?? '—'}
                      {r.paymentStatus === 'partial' ? <div>оплачено частично</div> : null}
                      {r.paymentStatus === 'full' ? <div>оплачено</div> : null}
                    </td>
                    <td className="num py-2.5 pr-5">{date(r.requiredDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </Panel>
      )}
    </div>
  );
}

const PROC_TABS = [
  { key: 'requests', title: 'Заявки' },
  { key: 'rfqs', title: 'Запросы КП' },
  { key: 'registries', title: 'Реестры' },
  { key: 'payments', title: 'Заявки на оплату' },
] as const;
type ProcTab = (typeof PROC_TABS)[number]['key'];

/** Сроки этапов заявки в рабочих днях: настройка администратора (ЗК-08) */
function StageSlaDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['procurement', 'stage-sla'],
    queryFn: () => api<Record<string, number>>('/procurement/stage-sla'),
    enabled: open,
  });
  const [edit, setEdit] = useState<Record<string, string>>({});
  const value = (st: string) => edit[st] ?? String(q.data?.[st] ?? '');
  const save = useMutation({
    mutationFn: () =>
      api<Record<string, number>>('/procurement/stage-sla', {
        method: 'PUT',
        body: { days: Object.fromEntries(STAGE_SLA_STAGES.map((st) => [st, Number(value(st))])) },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast('Сроки этапов сохранены');
      setEdit({});
      onClose();
    },
  });
  const ok = STAGE_SLA_STAGES.every((st) => {
    const n = Number(value(st));
    return Number.isInteger(n) && n >= 1 && n <= 60;
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setEdit({});
          onClose();
        }
      }}
      title="Сроки этапов заявки"
      description="Рабочих дней на этап по календарю РК. После срока заявка считается просроченной."
      footer={
        <Button variant="primary" disabled={!ok || save.isPending || !q.data} onClick={() => save.mutate()}>
          Сохранить
        </Button>
      }
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {STAGE_SLA_STAGES.map((st) => (
            <Field key={st} label={PROCUREMENT_STAGES[st]} htmlFor={`sla-${st}`}>
              <Input
                id={`sla-${st}`}
                inputMode="numeric"
                type="number"
                min={1}
                max={60}
                className="h-11"
                value={value(st)}
                onChange={(e) => setEdit({ ...edit, [st]: e.target.value })}
              />
            </Field>
          ))}
        </div>
      )}
      {save.isError ? <ErrorBox error={save.error} className="mt-3" /> : null}
    </Dialog>
  );
}

export function ProcurementPage() {
  const [open, setOpen] = useState(false);
  const canCreate = useCan('procurement.request.create');
  const canSla = canTenant(useMe().access, 'admin.settings');
  const [slaOpen, setSlaOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Вкладка — в адресе: «назад» и «вперёд» переключают вкладки, ссылку на реестры можно переслать
  const params = useSearch({ strict: false }) as { tab?: string };
  const tab: ProcTab =
    params.tab === 'registries' || params.tab === 'payments' || params.tab === 'rfqs'
      ? params.tab
      : 'requests';
  const navigate = useNavigate();
  const pick = (t: ProcTab) =>
    void navigate({ to: '/procurement', search: t === 'requests' ? {} : { tab: t } });
  useCrumbs(tab === 'requests' ? [] : [{ label: PROC_TABS.find((x) => x.key === tab)!.title }]);
  return (
    <Page>
      <PageHeader
        title="Закупки"
        subtitle="Заявка → проверка → сбор КП → анализ и выбор → согласование → договор → реестр → оплата → поставка"
        actions={
          <div className="flex flex-wrap gap-2">
            {canSla ? (
              <Button icon={<Settings className="size-4" />} onClick={() => setSlaOpen(true)}>
                Сроки этапов
              </Button>
            ) : null}
            {canCreate ? (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
                Новая заявка
              </Button>
            ) : null}
          </div>
        }
      />
      {canSla ? <StageSlaDialog open={slaOpen} onClose={() => setSlaOpen(false)} /> : null}
      <ProcurementOverviewStrip />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div
          role="tablist"
          aria-label="Закупки"
          className="inline-flex rounded-md border border-line bg-surface p-0.5"
        >
          {PROC_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => pick(t.key)}
              className={clsx(
                'h-8 rounded-[5px] px-3 text-[13px] font-medium',
                tab === t.key ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
              )}
            >
              {t.title}
            </button>
          ))}
        </div>
        {tab === 'requests' ? (
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Номер, материал, поставщик, договор, 1С"
            aria-label="Поиск заявок"
            className="h-9 max-w-[380px]"
          />
        ) : null}
      </div>
      {tab === 'requests' ? (
        <RequestsBoard search={search.trim().length >= 2 ? search.trim() : undefined} />
      ) : null}
      {tab === 'rfqs' ? <RfqsTab /> : null}
      {tab === 'registries' ? <RegistriesTab /> : null}
      {tab === 'payments' ? <PaymentRequestsTab /> : null}
      <NewRequestDialog open={open} onOpenChange={setOpen} />
    </Page>
  );
}
